/* eslint-disable tailwindcss/classnames-order */
/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-unnecessary-arbitrary-value */
/* eslint-disable tailwindcss/no-arbitrary-value */
import { Icon, IconProps } from '../icon/Icon';
import StarBackground from './starBackground.svg';
import { cn } from '@ui/lib';
import { cva, VariantProps } from 'class-variance-authority';

const pictogramVariants = cva('inline-flex items-center justify-center', {
  variants: {
    type: {
      brand: 'brand',
      positive: 'positive',
      negative: 'negative',
      warning: 'warning',
      accent1: 'accent-1',
      accent2: 'accent-2',
      accent3: 'accent-3',
      accent4: 'accent-4',
      accent5: 'accent-5',
      neutral: 'secondary-surface',
      neutralSubdued: 'secondary-surface-subdued',
      outlined: 'primary-surface',
      primarySurface: 'secondary-surface',
    },
    color: {
      teal: 'teal',
      emerald: 'emerald',
      ruby: 'ruby',
      citrus: 'citrus',
      grape: 'grape',
      lagoon: 'lagoon',
      lime: 'lime',
      tangerine: 'tangerine',
      piggybank: 'piggybank',
    },
    shape: {
      circle: 'rounded-full icon-shape ',
      square: 'rounded-2xl icon-shape ',
      star: 'icon-star-shape',
    },
    inverse: {
      true: 'inverse',
      false: '',
    },
    noBackground: {
      true: 'no-bg',
      false: '',
    },
  },
  defaultVariants: {
    shape: 'circle',
  },
});

export interface PictogramProps extends VariantProps<typeof pictogramVariants> {
  icon?: Pick<IconProps, 'fileName'>;
  size: IconProps['size'];
  children?: (colorClass: string) => React.ReactNode;
  className?: string;
}

export const sizeMapping = {
  xxs: ['sm', 16],
  xs: ['xs-sm', 24],
  sm: ['md', 32],
  md: ['lg', 40],
  lg: ['lg', 64],
  xl: ['xl', 88],
  // @TODO: Re-evaluate. https://www.figma.com/design/p55XGLyohSHjLWqbMh4x31?node-id=3088-62754&m=dev#1225074472
  '4xl': ['4xl', 120],
} as Record<string, [IconProps['size'], number]>;

export const classMapping = {
  xxs: 'size-[16px]',
  xs: 'size-[24px]',
  sm: 'size-[32px]',
  md: 'size-[40px]',
  lg: 'size-[64px]',
  xl: 'size-[88px]',
  // @TODO: Re-evaluate. https://www.figma.com/design/p55XGLyohSHjLWqbMh4x31?node-id=3088-62754&m=dev#1225074472
  '4xl': 'size-[120px]',
} as Record<string, string>;

const renderChild = (
  props: PictogramProps,
  colorClass: string,
  childSize: IconProps['size'],
) => {
  if (props.icon) {
    return <Icon {...props.icon} size={childSize} />;
  }
  return props.children?.(colorClass);
};

const starShape = (
  props: PictogramProps,
  colorClass: string,
  parentSize: number,
  childSize: IconProps['size'],
) => {
  return (
    <div
      style={{
        width: `${parentSize}px`,
        height: `${parentSize}px`,
      }}
      className="inline-flex items-center justify-center"
    >
      <StarBackground
        style={{
          width: `${parentSize}px`,
          height: `${parentSize}px`,
        }}
        className={cn(
          'absolute',
          pictogramVariants({
            shape: 'star',
            type: props.type,
            color: props.color,
            inverse: props.inverse,
            noBackground: props.noBackground,
          }),
        )}
      />
      <div
        className={cn(
          'relative ',
          pictogramVariants({
            type: props.type,
            color: props.color,
            inverse: props.inverse,
            noBackground: props.noBackground,
          }),
          props.className,
        )}
      >
        {renderChild(props, colorClass, childSize)}
      </div>
    </div>
  );
};

export const Pictogram = (props: PictogramProps) => {
  const [childSize, parentSize] = sizeMapping[props.size ?? 'md'] as [
    IconProps['size'],
    number,
  ];

  const colorClass = pictogramVariants({
    type: props.type,
    color: props.color,
    shape: props.shape,
    inverse: props.inverse,
    noBackground: props.noBackground,
  });

  if (props.shape === 'star') {
    return starShape(
      props,
      colorClass,
      parentSize,
      childSize ? childSize : 'md',
    );
  }

  return (
    <div
      style={{
        width: `${parentSize}px`,
        height: `${parentSize}px`,
      }}
      className={cn(
        pictogramVariants({
          type: props.type,
          color: props.color,
          shape: props.shape,
          inverse: props.inverse,
          noBackground: props.noBackground,
        }),
        props.className,
      )}
    >
      {renderChild(props, colorClass, childSize)}
    </div>
  );
};
